import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';

export interface StatCardTrend {
  changePercent: number | null;
  /** When false, an increase is shown as negative (e.g. overdue milestones). */
  positiveIsGood?: boolean;
}

interface StatCardProps {
  label: string;
  value: string | number | undefined;
  hint?: string;
  icon?: ReactNode;
  iconClassName?: string;
  trend?: StatCardTrend;
  loading?: boolean;
  className?: string;
  onClick?: () => void;
  /** Escape hatch for per-instance custom properties. */
  style?: CSSProperties;
}

const formatTrend = (trend: StatCardTrend): { text: string; className: string } => {
  const positiveIsGood = trend.positiveIsGood ?? true;

  if (trend.changePercent === null) {
    return { text: 'New this month', className: 'text-success' };
  }

  if (trend.changePercent === 0) {
    return { text: 'No change from last month', className: 'text-muted-foreground' };
  }

  const increased = trend.changePercent > 0;
  const favorable = positiveIsGood ? increased : !increased;
  const direction = increased ? 'increase' : 'decrease';

  return {
    text: `${Math.abs(trend.changePercent)}% ${direction} from last month`,
    className: favorable ? 'text-success' : 'text-destructive',
  };
};

export function StatCard({
  label,
  value,
  hint,
  icon,
  iconClassName,
  trend,
  loading = false,
  className,
  style,
  onClick,
}: StatCardProps) {
  const trendDisplay = trend ? formatTrend(trend) : null;
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    onClick();
  };

  return (
    <Card
      elevation="soft"
      interactive={Boolean(onClick)}
      className={cn('gap-0 py-5', onClick && 'cursor-pointer', className)}
      style={style}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent className="flex flex-col gap-2 px-5">
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          {icon ? (
            <div
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-md [&>svg]:size-5',
                iconClassName,
              )}
            >
              {icon}
            </div>
          ) : null}
        </div>

        {loading ? (
          <Skeleton className="h-9 w-20" />
        ) : (
          <span className="text-figure tabular-nums text-foreground">{value ?? 0}</span>
        )}

        {loading ? (
          <Skeleton className="h-4 w-36" />
        ) : trendDisplay ? (
          <span className={cn('text-xs leading-relaxed', trendDisplay.className)}>
            {trendDisplay.text}
          </span>
        ) : hint ? (
          <span className="text-xs leading-relaxed text-muted-foreground">{hint}</span>
        ) : null}
      </CardContent>
    </Card>
  );
}
